import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.session_token) localStorage.setItem("kb_session_token", res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    if (res.data.session_token) localStorage.setItem("kb_session_token", res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("kb_session_token");
    sessionStorage.removeItem("kb_onboarding_role");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function dashboardHomeFor(role) {
  switch (role) {
    case "admin": return "/admin";
    case "mentor":
    case "tutor": return "/mentor";
    case "partner_institution": return "/institution";
    default: return "/dashboard";
  }
}

// After successful auth, route to onboarding profile if not complete, else role home
export function postAuthDestination(user) {
  if (!user) return "/login";
  // Admins bypass every gate.
  if (user.role === "admin" || user.role === "super_admin") return "/admin";
  // Email verification is mandatory for every non-admin user (except those
  // whose email was pre-verified via admin action or vacancies onboarding).
  if (user.email_verified !== true) return "/account/verify-email";
  // Privileged staff roles (mentors/tutors/partners/employees/interns) must
  // additionally wait for admin approval — unless they were onboarded from
  // /admin/vacancies (in which case `verified` is preset to True).
  const privileged = ["mentor", "tutor", "partner_institution", "employee", "intern"];
  if (privileged.includes(user.role) && user.verified !== true) return "/account/pending-verification";
  if (user.profile_complete === false) return "/onboarding/profile";
  if (user.force_password_reset) return "/account/reset-password";
  return dashboardHomeFor(user.role);
}

export const ROLE_LABELS = {
  client: "Client / Researcher",
  student: "Student",
  mentor: "Mentor",
  tutor: "Tutor / LMS Author",
  partner_institution: "Partner Institution",
  admin: "Admin",
};
